<script>
import Dropdown from "primevue/dropdown";
import {nextTick} from "vue";

/**
 * AdaptersDropdown — расширенный компонент PrimeVue Dropdown с поддержкой управления через наведение (hover).
 *
 * Основные возможности:
 * - Поддержка hover-режима (по умолчанию отключен, включается через `hoverUsage`)
 * - Автоматическое открытие/закрытие дропдауна при наведении на input или панель
 * - Корректная работа на мыши, тачпаде, сенсорных устройствах и клавиатуре
 * - Поддержка `focusin/focusout` для клавиатурной навигации
 * - Безопасная работа с DOM через `nextTick`
 * - Очистка слушателей при размонтировании
 *
 * Пропсы:
 * - `hoverUsage: Boolean` — включает/отключает управление через наведение (по умолчанию: `false`)
 * - `appendTo: String` — селектор родительского элемента для телепорта панели (по умолчанию: `#keysoApp`)
 *
 * Совместимость:
 * - Поддерживает все основные браузеры, мобильные и десктопные устройства
 * - Учитывает события: `pointer`, `mouse`, `touch`, `focus`, `window blur`
 *
 * Использование:
 * <AdaptersDropdown v-model="selected" :options="options" :hoverUsage="true" />
 */
export default {
    name: "AdaptersDropdown",
    extends: Dropdown,
    props: {
        hoverUsage: {
            type: Boolean,
            required: false,
            default: false,
        },
        appendTo: {
            type: String,
            required: false,
            default: '#keysoApp',
        }
    },
    data() {
        return {
            hover: {
                input: false,
                panel: false,
            },
            hoverTimeouts: {
                input: null,
                panel: null,
            },
            listenersAttached: false,
            skipHoverWatcher: false,
        }
    },
    mounted() {
        if (this.hoverUsage) {
            nextTick(() => {
                this.addInputListeners();
                this.addListener(window, 'blur', this.onWindowBlur);
            });
        }
    },
    beforeUnmount() {
        Object.values(this.hoverTimeouts).forEach(clearTimeout);
        this.removeAllListeners();
    },
    methods: {
        // Дополнен наследуемый метод
        onOverlayAfterEnter() {
            if (this.hoverUsage) {
                nextTick(() => {
                    this.addPanelListeners();
                });
            }

            // вызываем оригинальный метод PrimeVue Dropdown
            Dropdown.methods.onOverlayAfterEnter.call(this);
        },
        // Дополнен наследуемый метод
        updateModel(event, value) {
            // Вызов оригинального метода PrimeVue Dropdown
            Dropdown.methods.updateModel.call(this, event, value);
            if (!this.hoverUsage) {
                return;
            }

            // Игнорируем следующий hover-триггер
            this.skipHoverWatcher = true;

            // Сбрасываем состояние через метод
            this.setHoverState('input', false);
            this.setHoverState('panel', false);

            // Принудительно скрываем
            this.hide(false);

            // Восстановим реакцию на hover после короткой паузы
            setTimeout(() => {
                this.skipHoverWatcher = false;
            }, 300);
        },
        // ============ INPUT LISTENERS ============
        addInputListeners() {
            const container = this.$refs.container;
            if (!container || this.listenersAttached) {
                return;
            }

            // Pointer & Mouse
            this.addListener(container, 'pointerenter', this.onInputEnter);
            this.addListener(container, 'pointerleave', this.onInputLeave);
            this.addListener(container, 'mouseenter', this.onInputEnter);
            this.addListener(container, 'mouseleave', this.onInputLeave);

            // Touch
            this.addListener(container, 'touchstart', this.onTouchStart, { passive: false });

            // Focus
            this.addListener(container, 'focusin', this.onInputEnter);
            this.addListener(container, 'focusout', this.onInputLeave);

            this.listenersAttached = true;
        },
        addPanelListeners() {
            const panel = this.overlay;
            if (!panel) {
                return;
            }

            this.addListener(panel, 'pointerenter', this.onPanelEnter);
            this.addListener(panel, 'pointerleave', this.onPanelLeave);
            this.addListener(panel, 'mouseenter', this.onPanelEnter);
            this.addListener(panel, 'mouseleave', this.onPanelLeave);
        },
        removeAllListeners() {
            const container = this.$refs.container;
            if (!container) {
                return;
            }

            this.removeListener(container, 'pointerenter', this.onInputEnter);
            this.removeListener(container, 'pointerleave', this.onInputLeave);
            this.removeListener(container, 'mouseenter', this.onInputEnter);
            this.removeListener(container, 'mouseleave', this.onInputLeave);
            this.removeListener(container, 'touchstart', this.onTouchStart);
            this.removeListener(container, 'focusin', this.onInputEnter);
            this.removeListener(container, 'focusout', this.onInputLeave);

            if (this.overlay) {
                this.removeListener(this.overlay, 'pointerenter', this.onPanelEnter);
                this.removeListener(this.overlay, 'pointerleave', this.onPanelLeave);
                this.removeListener(this.overlay, 'mouseenter', this.onPanelEnter);
                this.removeListener(this.overlay, 'mouseleave', this.onPanelLeave);
            }

            window.removeEventListener('blur', this.onWindowBlur);
        },
        addListener(target, event, handler, options = {}) {
            target.addEventListener(event, handler, options);
        },
        removeListener(target, event, handler) {
            target.removeEventListener(event, handler);
        },
        // ============ EVENTS ============
        onInputEnter() {
            this.setHoverState('input', true);
        },
        onInputLeave() {
            this.setHoverState('input', false);
        },
        onPanelEnter() {
            this.setHoverState('panel', true);
        },
        onPanelLeave() {
            this.setHoverState('panel', false);
        },
        onTouchStart(e) {
            if (e.cancelable) {
                e.preventDefault();
            }

            if (!this.overlayVisible) {
                this.show(false);
            }
        },
        onWindowBlur() {
            this.hide(false);
        },

        setHoverState(part, value) {
            clearTimeout(this.hoverTimeouts[part]);

            if (value) {
                // Открытие — сразу, принудительно триггерим реактивность
                this.hover = { ...this.hover, [part]: true };
                return;
            }

            // Закрытие — с задержкой
            this.hoverTimeouts[part] = setTimeout(() => {
                this.hover = { ...this.hover, [part]: false };
            }, 100);
        }
    },
    watch: {
        hover: {
            handler() {
                if (!this.hoverUsage || this.skipHoverWatcher) {
                    return;
                }

                if (!this.hover.input && !this.hover.panel && !navigator.maxTouchPoints) {
                    this.hide(false);
                    return;
                }

                this.show(false);
            },
            deep: true,
        }
    },
}
</script>
